import Cookies from 'js-cookie';
import { COOKIES } from '@/constants';

const DEFAULT_REDIRECT_PATH = '/home';
const isSafeRelativePath = (path: string) => path.startsWith('/') && !path.startsWith('//');

const LAST_EMAIL_KEY = 'auth.last_email';

/**
 * Loop guard shared by the login page and the session provider. AuthLogin stamps this the moment it
 * redirects an already-authenticated visitor into the app; SessionProvider clears it once the app has
 * actually loaded with a session. If the stamp is still present when the login page remounts, /home
 * bounced the visitor back (an orphaned Better Auth session with no domain session) and the login page
 * breaks the loop instead of redirecting again.
 */
export const LOGIN_REDIRECT_GUARD_KEY = 'auth.login.redirected-at';
export const LOGIN_REDIRECT_GUARD_WINDOW_MS = 10_000;

export const clearLoginRedirectGuard = () => {
    try {
        sessionStorage.removeItem(LOGIN_REDIRECT_GUARD_KEY);
    } catch {
        // sessionStorage unavailable — nothing to clear.
    }
};

/**
 * Cross-tab logout signal. Writing this `localStorage` key fires a `storage` event in every OTHER
 * same-origin tab (it never fires in the tab that writes it), so any open Swonkie tab can react to a
 * logout or account deletion immediately instead of waiting for its next guarded request. The value is
 * only a timestamp to force a change event; listeners react to the key, not the value.
 */
export const AUTH_LOGOUT_BROADCAST_KEY = 'auth:logout';

export const broadcastLogout = () => {
    try {
        localStorage.setItem(AUTH_LOGOUT_BROADCAST_KEY, String(Date.now()));
    } catch {
        // localStorage unavailable — cross-tab logout is best-effort.
    }
};

export const getLastUsedEmail = (): string | null => {
    try {
        return localStorage.getItem(LAST_EMAIL_KEY);
    } catch {
        return null;
    }
};

export const setLastUsedEmail = (email: string) => {
    try {
        localStorage.setItem(LAST_EMAIL_KEY, email);
    } catch {
        // ignore quota / security errors
    }
};

// The login page opens on the password step for the remembered email, so switching accounts must
// forget it, otherwise the previous account is one autofilled password away.
export const clearLastUsedEmail = () => {
    try {
        localStorage.removeItem(LAST_EMAIL_KEY);
    } catch {
        // ignore security errors
    }
};

const normalizeRedirectPath = (value?: string | null) => {
    if (!value) {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    if (isSafeRelativePath(trimmed)) {
        return trimmed;
    }

    try {
        const parsed = new URL(trimmed);
        if (parsed.origin !== window.location.origin) {
            return null;
        }

        const normalizedPath = `${parsed.pathname}${parsed.search}`;
        return isSafeRelativePath(normalizedPath) ? normalizedPath : null;
    } catch {
        return null;
    }
};

export const getRedirectPath = () => {
    const params = new URLSearchParams(window.location.search);
    const fromParam = normalizeRedirectPath(params.get('redirectTo'));
    if (fromParam) {
        return fromParam;
    }

    const fromCookie = normalizeRedirectPath(Cookies.get(COOKIES.REDIRECT_AFTER_LOGIN));
    if (fromCookie) {
        return fromCookie;
    }

    return DEFAULT_REDIRECT_PATH;
};

export const clearRedirectCookie = () => {
    Cookies.remove(COOKIES.REDIRECT_AFTER_LOGIN);
};

export const redirectAfterAuth = (path?: string | null) => {
    const target = normalizeRedirectPath(path) || getRedirectPath();
    clearRedirectCookie();
    window.location.replace(target);
};

export const getCurrentRelativeUrl = () => `${window.location.pathname}${window.location.search}`;

type AuthUrlOpts = { email?: string; lockEmail?: boolean };

const buildAuthUrl = (base: string, redirectTo: string, opts?: AuthUrlOpts) => {
    const params = new URLSearchParams();
    params.set('redirectTo', redirectTo);
    if (opts?.email) {
        params.set('email', opts.email);
    }
    if (opts?.lockEmail) {
        params.set('lockEmail', '1');
    }
    return `${base}?${params.toString()}`;
};

export const getAuthLoginUrl = (redirectTo: string, opts?: AuthUrlOpts) =>
    buildAuthUrl('/auth/login', redirectTo, opts);

export const getAuthRegisterUrl = (redirectTo: string, opts?: AuthUrlOpts) =>
    buildAuthUrl('/auth/register', redirectTo, opts);

/**
 * Reads a pre-fill email and lock flag from the query string. Used by the invite flow to carry the
 * invited email into login/register (`?email=…&lockEmail=1`) so it can be pre-filled and made read-only.
 */
export const getEmailParams = (): { email?: string; lockEmail: boolean } => {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email')?.trim();
    const lockEmail = params.get('lockEmail') === '1' || params.get('lockEmail') === 'true';
    return { email: email && email.includes('@') ? email : undefined, lockEmail };
};

/**
 * Decodes the (unverified) invite `hint` JWT payload `{ e: email, o: orgName, exp }` for UI pre-fill.
 * The signature is NOT verified here — the server validates the invitation on accept; this is UX only.
 */
export const decodeInviteHint = (
    hint: string | null | undefined,
): { email: string; organizationName?: string } | null => {
    if (!hint) {
        return null;
    }
    try {
        const payload = hint.split('.')[1];
        if (!payload) {
            return null;
        }
        const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
        const json = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
                .join(''),
        );
        const parsed = JSON.parse(json) as { e?: unknown; o?: unknown };
        if (typeof parsed.e !== 'string' || !parsed.e) {
            return null;
        }
        return { email: parsed.e, organizationName: typeof parsed.o === 'string' ? parsed.o : undefined };
    } catch {
        return null;
    }
};

/**
 * Returns the clean login URL to use as an OAuth errorCallbackURL.
 * Avoids embedding the current URL's own ?redirectTo so better-auth
 * doesn't append &error= to a URL that already has a nested redirectTo loop.
 */
export const getAuthErrorCallbackUrl = () => `/auth/login`;

/**
 * Better Auth resolves a *relative* social `callbackURL`/`errorCallbackURL` against the request origin —
 * which for the OAuth flow is the backend API (it lands on `${BETTER_AUTH_URL}/auth/callback/:provider`).
 * A bare `/home` would therefore return the user to the backend after login. Force an absolute URL on the
 * frontend origin so social/SSO logins land back on the web app. Absolute inputs are passed through.
 */
export const toAbsoluteAppUrl = (pathOrUrl: string): string => {
    try {
        return new URL(pathOrUrl).href; // already absolute (has a scheme)
    } catch {
        return `${window.location.origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
    }
};

/**
 * Reads the `?error=` and optional `?error_description=` query params appended by better-auth when an
 * OAuth callback fails. Uses the LAST value of each: a return URL can carry a stale `error` from a
 * previous attempt, and better-auth appends the new one — so the most recent is the real cause.
 */
export const getOAuthError = (): { code: string; description: string | null } | null => {
    const params = new URLSearchParams(window.location.search);
    const codes = params.getAll('error');
    const code = codes[codes.length - 1];
    if (!code) return null;
    const descriptions = params.getAll('error_description');
    return { code, description: descriptions[descriptions.length - 1] ?? null };
};

/**
 * Returns the signed OAuth query string from Better Auth redirect pages.
 *
 * This must be forwarded to /auth/sign-in/* or /auth/sign-up/* calls so the
 * oauth-provider plugin can resume the pending authorization flow after login.
 */
export const getOAuthQueryFromSearch = (): string | undefined => {
    const params = new URLSearchParams(window.location.search);

    const hasRequiredParams =
        params.has('client_id') && params.has('redirect_uri') && params.has('exp') && params.has('sig');

    return hasRequiredParams ? params.toString() : undefined;
};
